import React, { FC, useState, useEffect, useCallback } from 'react';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import { icon } from 'leaflet';
import mapPinIcon from '@assets/images/map-pin.svg';
import { useTranslation } from 'react-i18next';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Select, SelectType, SimpleOptionsList } from '@components/atoms/Select';
import { MultiSelect, MultiSelectType } from '@components/atoms/MultiSelect';
import { Button, ButtonMode } from '@components/atoms/Button';
import { SpaceType, SpaceStatus, CreateAlertInput } from '@types';
import styles from './SpacesFilter.module.css';
import { useGetSpaceTypes } from '@services/useGetSpaceTypes';
import { useGetSpaceStatuses } from '@services/useGetSpaceStatuses';
import { useCreateAlert } from '@services/useCreateAlert';
import { useGetMaxPrice } from '@services/useGetMaxPrice';
import { useNotifications } from '@hooks/useNotifications';
import { useData } from '@hooks/useData';

export interface SpacesFilterValues {
    searchQuery?: string;
    featuredType?: string;
    propertyTypes?: string[];
    areaMin?: number;
    areaMax?: number;
    propertyCondition?: string;
    businessCategory?: string;
    priceMin?: number;
    priceMax?: number;
}

export interface SpacesFilterProps {
    onApplyFilters: (filters: SpacesFilterValues) => void;
    onClearFilters: () => void;
    onCreateAlert?: () => void;
    initialValues?: SpacesFilterValues;
    businessCategoryOptions?: SimpleOptionsList;
    showMapToggle?: boolean;
    isMapVisible?: boolean;
    onToggleMap?: (visible: boolean) => void;
    mapPins?: [number, number][];
    showCreateAlert?: boolean;
    showPriceSlider?: boolean;
    showAreaFilter?: boolean;
}

const pinIcon = icon({
    iconUrl: mapPinIcon,
    iconSize: [38, 95],
    shadowSize: [50, 64],
    iconAnchor: [22, 94],
    shadowAnchor: [4, 62],
    popupAnchor: [-3, -76]
});

export const SpacesFilter: FC<SpacesFilterProps> = ({
                                                        onApplyFilters,
                                                        onClearFilters,
                                                        onCreateAlert,
                                                        initialValues = {},
                                                        businessCategoryOptions = [],
                                                        showMapToggle = false,
                                                        isMapVisible = true,
                                                        onToggleMap,
                                                        mapPins = [],
                                                        showCreateAlert = true,
                                                        showPriceSlider = true,
                                                        showAreaFilter = true,
                                                    }) => {
    const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const checkIsMobile = useCallback(() => {
        setIsMobile(window.innerWidth < 768);
    }, []);

    useEffect(() => {
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, [checkIsMobile]);

    const toggleFiltersExpanded = () => {
        setIsFiltersExpanded(!isFiltersExpanded);
    };

    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();

    const { user } = useData();

    const isPublicUser = user?.data?.user_type_id === 5;

    const { request: fetchSpaceTypes, spaceTypes } = useGetSpaceTypes();
    const { request: fetchSpaceStatuses, spaceStatuses } = useGetSpaceStatuses();
    const { request: createAlert, loading: creatingAlert, success, error } = useCreateAlert();
    const { request: fetchMaxPrice, maxPrice } = useGetMaxPrice();

    const [searchQuery, setSearchQuery] = useState<string>(initialValues.searchQuery || '');
    const [featuredType, setFeaturedType] = useState<string>(initialValues.featuredType || '');
    const [propertyTypes, setPropertyTypes] = useState<string[]>(initialValues.propertyTypes || []);
    const [areaMin, setAreaMin] = useState<string>(initialValues.areaMin?.toString() || '');
    const [areaMax, setAreaMax] = useState<string>(initialValues.areaMax?.toString() || '');
    const [propertyCondition, setPropertyCondition] = useState<string>(initialValues.propertyCondition || '');
    const [businessCategory, setBusinessCategory] = useState<string>(initialValues.businessCategory || '');
    const [priceMin, setPriceMin] = useState<number>(initialValues.priceMin || 0);
    const [priceMax, setPriceMax] = useState<number>(initialValues.priceMax || 0);

    // Debounced values for text inputs and sliders
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(searchQuery);
    const [debouncedAreaMin, setDebouncedAreaMin] = useState<string>(areaMin);
    const [debouncedAreaMax, setDebouncedAreaMax] = useState<string>(areaMax);
    const [debouncedPriceMin, setDebouncedPriceMin] = useState<number>(priceMin);
    const [debouncedPriceMax, setDebouncedPriceMax] = useState<number>(priceMax);
    const priceStep = 50;
    const roundedMaxPrice = Math.ceil(maxPrice / priceStep) * priceStep;

    // Fetch space types and statuses on component mount
    useEffect(() => {
        void fetchSpaceTypes();
        void fetchSpaceStatuses();
        void fetchMaxPrice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (maxPrice > 0 && priceMax === 0) {
            const rounded = Math.ceil(maxPrice / priceStep) * priceStep;
            setPriceMax(rounded);
            setDebouncedPriceMax(rounded);
        }
    }, [maxPrice, priceMax, priceStep]);

    // Show success notification
    useEffect(() => {
        if (success) {
            showDefaultNotification({
                type: 'success',
                message: t('spaces-filter.alert-created-success') || 'Alerta criado com sucesso!',
            });
            onCreateAlert?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showDefaultNotification({
                type: 'error',
                message: error,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    // Debounce searchQuery
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Debounce areaMin
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAreaMin(areaMin);
        }, 500);

        return () => clearTimeout(timer);
    }, [areaMin]);

    // Debounce areaMax
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAreaMax(areaMax);
        }, 500);

        return () => clearTimeout(timer);
    }, [areaMax]);

    // Debounce priceMin
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedPriceMin(priceMin);
        }, 500);

        return () => clearTimeout(timer);
    }, [priceMin]);

    // Debounce priceMax
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedPriceMax(priceMax);
        }, 500);

        return () => clearTimeout(timer);
    }, [priceMax]);

    // Convert spaceTypes to options format
    const propertyTypeOptionsFromApi: SimpleOptionsList = spaceTypes.map((type: SpaceType) => ({
        value: type.uuid,
        text: type.name,
    }));

    // Convert spaceStatuses to options format
    const propertyConditionOptionsFromApi: SimpleOptionsList = spaceStatuses.map((status: SpaceStatus) => ({
        value: status.uuid,
        text: status.name,
    }));

    const featuredTypeOptions: SimpleOptionsList = [
        { value: '', text: 'Todos' },
        { value: 'sale', text: 'Vender' },
        { value: 'rent', text: 'Arrendar' },
    ];

    const propertyTypeOptions: SimpleOptionsList = propertyTypeOptionsFromApi;

    const defaultPropertyConditionOptions: SimpleOptionsList = [
        { value: '', text: t('spaces-filter.all-conditions') },
        ...propertyConditionOptionsFromApi,
    ];

    const defaultBusinessCategoryOptions: SimpleOptionsList = [
        { value: '', text: t('spaces-filter.all-categories') },
        ...businessCategoryOptions,
    ];

    const handleApplyFilters = () => {
        const filters: SpacesFilterValues = {
            searchQuery: debouncedSearchQuery || undefined,
            featuredType: featuredType || undefined,
            propertyTypes: propertyTypes.length > 0 ? propertyTypes : undefined,
            areaMin: debouncedAreaMin ? parseFloat(debouncedAreaMin) : undefined,
            areaMax: debouncedAreaMax ? parseFloat(debouncedAreaMax) : undefined,
            propertyCondition: propertyCondition || undefined,
            businessCategory: businessCategory || undefined,
            priceMin: debouncedPriceMin || undefined,
            priceMax: debouncedPriceMax || undefined
        };

        onApplyFilters(filters);
    };

    // Helper function to adjust price step based on value
    const adjustPriceStep = (value: number): number => {
        if (value <= 10000) {
            return Math.round(value / 100) * 100;
        } else {
            return Math.round(value / 10000) * 10000;
        }
    };

    const handlePriceMinChange = (value: number) => {
        const minGap = priceStep;
        const newMin = Math.min(value, priceMax - minGap);
        setPriceMin(Math.max(0, newMin));
    };

    const handlePriceMaxChange = (value: number) => {
        const minGap = priceStep;
        const newMax = Math.max(value, priceMin + minGap);
        setPriceMax(Math.min(roundedMaxPrice, newMax));
    };

    const getSliderBackground = () => {
        if (roundedMaxPrice === 0) return {};
        const minPercent = (priceMin / roundedMaxPrice) * 100;
        const maxPercent = (priceMax / roundedMaxPrice) * 100;
        return {
            background: `linear-gradient(to right, #ddd ${minPercent}%, #007bff ${minPercent}%, #007bff ${maxPercent}%, #ddd ${maxPercent}%)`
        };
    };

    const handleCreateAlert = async () => {
        if (!isPublicUser) return;

        const alertData: CreateAlertInput = {};

        if (propertyCondition) {
            alertData.spaceStatusUuid = propertyCondition;
        }
        if (businessCategory) {
            alertData.spaceTypeCategory = businessCategory;
        }
        if (debouncedAreaMin && parseFloat(debouncedAreaMin) > 0) {
            alertData.areaMin = parseFloat(debouncedAreaMin);
        }
        if (debouncedAreaMax && parseFloat(debouncedAreaMax) > 0) {
            alertData.areaMax = parseFloat(debouncedAreaMax);
        }
        if (debouncedPriceMin > 0) {
            alertData.priceMin = debouncedPriceMin;
        }
        if (debouncedPriceMax > 0) {
            alertData.priceMax = debouncedPriceMax;
        }
        if (propertyTypes.length > 0) {
            alertData.spaceTypeUuids = propertyTypes;
        }

        try {
            await createAlert(alertData);
        } catch (error) {
            // Error is already handled by useEffect
        }
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setFeaturedType('');
        setPropertyTypes([]);
        setAreaMin('');
        setAreaMax('');
        setPropertyCondition('');
        setBusinessCategory('');
        setPriceMin(0);
        setPriceMax(maxPrice);
        onClearFilters();
    };

    // Apply filters automatically when any filter changes (using debounced values for text inputs and sliders)
    useEffect(() => {
        handleApplyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery, featuredType, propertyTypes, debouncedAreaMin, debouncedAreaMax, propertyCondition, businessCategory, debouncedPriceMin, debouncedPriceMax]);

    const renderMobileFilters = () => (
        <div className={styles['spaces-filter']}>
            {/* Mobile: 4 filtros principais */}
            <div className={`${styles['filter-row']} row g-3 mb-3`}>
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.searchInput}
                        placeholder={t('spaces-filter.search-placeholder') || 'Pesquisar...'}
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />
                </div>
                <div className="col-12">
                    <Select
                        width={'100%'}
                        type={SelectType.variant1}
                        options={featuredTypeOptions}
                        value={featuredType}
                        onChange={setFeaturedType}
                    />
                </div>
                <div className="col-12">
                    <Select
                        width={'100%'}
                        type={SelectType.variant1}
                        options={defaultBusinessCategoryOptions}
                        value={businessCategory}
                        onChange={setBusinessCategory}
                    />
                </div>
                <div className="col-12">
                    <MultiSelect
                        type={MultiSelectType.variant1}
                        options={propertyTypeOptions}
                        value={propertyTypes}
                        onChange={setPropertyTypes}
                        placeholder={t('spaces-filter.property-type-placeholder') || 'Tipo de imóvel'}
                    />
                </div>
            </div>

            {/* Botao FILTROS */}
            <div className={`${styles['filter-row']} row g-3 mb-3`}>
                <div className="col-12">
                    <Button
                        classes={'w-100'}
                        icon="fa-solid fa-sliders"
                        mode={ButtonMode.primary}
                        onClick={toggleFiltersExpanded}
                        label={t('spaces-filter.filters-button') || 'FILTROS'}
                    />
                </div>
            </div>

            {/* Toggle Ver Mapa */}
            {showMapToggle && (
                <div className={`${styles['filter-row']} ${styles['map-toggle-row']} row g-3 mb-3`}>
                    <div className="col-12 d-flex align-items-center justify-content-center">
                        <span className={styles['map-toggle-label']}>{t('spaces-filter.view-map') || 'Ver mapa'}</span>
                        <label className={styles['toggle-switch']}>
                            <input
                                type="checkbox"
                                checked={isMapVisible}
                                onChange={(e) => onToggleMap && onToggleMap(e.target.checked)}
                            />
                            <span className={styles['toggle-slider']}></span>
                        </label>
                    </div>
                </div>
            )}

            {/* Mapa - aparece logo apos o toggle no mobile */}
            {showMapToggle && isMapVisible && mapPins.length > 0 && (
                <div className={`${styles['filter-row']} row g-3 mb-3`}>
                    <div className="col-12">
                        <div className={styles['mobile-map-container']}>
                            <MapContainer center={mapPins[0]} zoom={7} scrollWheelZoom={false}>
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                {mapPins.map((pin, idx) => (
                                    <Marker icon={pinIcon} key={idx} position={pin}>
                                        <span>Marker</span>
                                    </Marker>
                                ))}
                            </MapContainer>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal/Drawer de filtros expandidos */}
            {isFiltersExpanded && (
                <div className={styles['filters-modal-overlay']} onClick={toggleFiltersExpanded}>
                    <div className={styles['filters-modal']} onClick={(e) => e.stopPropagation()}>
                        <div className={styles['filters-modal-header']}>
                            <button
                                className={styles['filters-modal-close']}
                                onClick={toggleFiltersExpanded}
                                aria-label="Fechar"
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles['filters-modal-content']}>
                            {/* Pesquisa */}
                            <div className="mb-3">
                                <Input
                                    type={InputTypes.text}
                                    variant={InputVariants.searchInput}
                                    placeholder={t('spaces-filter.search-placeholder') || 'Pesquisar...'}
                                    value={searchQuery}
                                    onChange={setSearchQuery}
                                />
                            </div>
                            {/* Tipo de espaco */}
                            <div className="mb-3">
                                <Select
                                    width={'100%'}
                                    type={SelectType.variant1}
                                    options={featuredTypeOptions}
                                    value={featuredType}
                                    onChange={setFeaturedType}
                                />
                            </div>
                            {/* Localizacao */}
                            <div className="mb-3">
                                <Select
                                    width={'100%'}
                                    type={SelectType.variant1}
                                    options={defaultBusinessCategoryOptions}
                                    value={businessCategory}
                                    onChange={setBusinessCategory}
                                />
                            </div>
                            {/* Tipo de imovel */}
                            <div className="mb-3">
                                <MultiSelect
                                    type={MultiSelectType.variant1}
                                    options={propertyTypeOptions}
                                    value={propertyTypes}
                                    onChange={setPropertyTypes}
                                    placeholder={t('spaces-filter.property-type-placeholder') || 'Tipo de imóvel'}
                                />
                            </div>
                            {/* Area Min/Max */}
                            {showAreaFilter && (
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <Input
                                            type={InputTypes.number}
                                            variant={InputVariants.regular}
                                            placeholder={t('spaces-filter.area-min') || 'Min - Área m²'}
                                            value={areaMin}
                                            onChange={setAreaMin}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <Input
                                            type={InputTypes.number}
                                            variant={InputVariants.regular}
                                            placeholder={t('spaces-filter.area-max') || 'Max - Área m²'}
                                            value={areaMax}
                                            onChange={setAreaMax}
                                        />
                                    </div>
                                </div>
                            )}
                            {/* Estado do imovel */}
                            <div className="mb-3">
                                <Select
                                    width={'100%'}
                                    type={SelectType.variant1}
                                    options={defaultPropertyConditionOptions}
                                    value={propertyCondition}
                                    onChange={setPropertyCondition}
                                />
                            </div>
                            {/* Categoria do negocio */}
                            <div className="mb-3">
                                <Select
                                    width={'100%'}
                                    type={SelectType.variant1}
                                    options={defaultBusinessCategoryOptions}
                                    value={businessCategory}
                                    onChange={setBusinessCategory}
                                />
                            </div>
                            {/* Intervalo de preco */}
                            {showPriceSlider && (
                                <div className="mb-3">
                                    <div className={styles['price-slider-container']}>
                                        <label className={styles['slider-label']}>
                                            {t('spaces-filter.price-range') || 'Intervalo de preço'}: <span className={styles['price-range-value']}>De: 0 a {maxPrice.toLocaleString('pt-PT')}€</span>
                                        </label>
                                        <div className={styles['slider-inputs']}>
                                            <input
                                                type="range"
                                                min="0"
                                                max={maxPrice}
                                                step="1"
                                                value={priceMin}
                                                onChange={(e) => handlePriceMinChange(Number(e.target.value))}
                                                className={styles['range-slider']}
                                            />
                                            <input
                                                type="range"
                                                min={priceMin}
                                                max={maxPrice}
                                                step={priceStep}
                                                value={priceMax}
                                                onChange={(e) => handlePriceMaxChange(Number(e.target.value))}
                                                className={styles['range-slider']}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Botao Pesquisar */}
                            <div className="mb-3">
                                <Button
                                    classes={'w-100'}
                                    mode={ButtonMode.primary}
                                    onClick={() => {
                                        handleApplyFilters();
                                        toggleFiltersExpanded();
                                    }}
                                    label={t('spaces-filter.search-button') || 'PESQUISAR'}
                                />
                            </div>
                            {/* Botao Criar Alerta */}
                            {isPublicUser && user && (
                            <div className="mb-3">
                                <Button
                                    classes={'w-100'}
                                    icon="fa-solid fa-bell"
                                    mode={ButtonMode.primary}
                                    outline
                                    onClick={handleCreateAlert}
                                    label={t('spaces-filter.create-alert') || 'CRIAR ALERTA'}
                                    disabled={creatingAlert}
                                />
                            </div>
                            )}
                            {/* Botao Limpar */}
                            <div className="mb-3">
                                <Button
                                    classes={'w-100'}
                                    mode={ButtonMode.primary}
                                    outline
                                    icon="fa-solid fa-trash"
                                    onClick={handleClearFilters}
                                    label={t('spaces-filter.clear') || 'LIMPAR'}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderDesktopFilters = () => (
        <div className={styles['spaces-filter']}>
            {/* First Row: Search, Featured Type (Arrendar/Vender), Property Type */}
            <div className={`${styles['filter-row']} row g-3 mb-3`}>
                <div className="col-12 col-md-3">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.searchInput}
                        placeholder={t('spaces-filter.search-placeholder') || 'Pesquisar...'}
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />
                </div>
                <div className="col-12 col-md-3">
                    <Select
                        width={'100%'}
                        type={SelectType.variant1}
                        options={featuredTypeOptions}
                        value={featuredType}
                        onChange={setFeaturedType}
                    />
                </div>
                <div className="col-12 col-md-3">
                    <MultiSelect
                        type={MultiSelectType.variant1}
                        options={propertyTypeOptions}
                        value={propertyTypes}
                        onChange={setPropertyTypes}
                        placeholder={t('spaces-filter.property-type-placeholder') || 'Tipo de imóvel'}
                    />
                </div>
            </div>

            {/* Second Row: Area Min/Max, Property Condition, Business Category */}
            <div className={`${styles['filter-row']} row g-3 mb-3`}>
                {showAreaFilter && (
                    <>
                        <div className="col-6 col-md-3">
                            <Input
                                type={InputTypes.number}
                                variant={InputVariants.regular}
                                placeholder={t('spaces-filter.area-min') || 'Área mín (m²)'}
                                value={areaMin}
                                onChange={setAreaMin}
                            />
                        </div>
                        <div className="col-6 col-md-3">
                            <Input
                                type={InputTypes.number}
                                variant={InputVariants.regular}
                                placeholder={t('spaces-filter.area-max') || 'Área máx (m²)'}
                                value={areaMax}
                                onChange={setAreaMax}
                            />
                        </div>
                    </>
                )}
                <div className={showAreaFilter ? 'col-12 col-md-3' : 'col-12 col-md-6'}>
                    <Select
                        type={SelectType.variant1}
                        options={defaultPropertyConditionOptions}
                        value={propertyCondition}
                        onChange={setPropertyCondition}
                    />
                </div>
                <div className={showAreaFilter ? 'col-12 col-md-3' : 'col-12 col-md-6'}>
                    <Select
                        type={SelectType.variant1}
                        options={defaultBusinessCategoryOptions}
                        value={businessCategory}
                        onChange={setBusinessCategory}
                    />
                </div>
            </div>

            {/* Third Row: Price Slider, Create Alert Button, Clear Button */}
            <div className={`${styles['filter-row']} row g-3 align-items-center`}>
                <div className="col-12 col-md-6">
                    <div className={styles['price-slider-container']}>
                        <label className={styles['slider-label']}>
                            {t('spaces-filter.price-range') || 'Intervalo de Preço'}: €{priceMin} - €{priceMax}
                        </label>
                        <div className={styles['slider-inputs']} style={getSliderBackground()}>
                            <input
                                type="range"
                                min="0"
                                max={roundedMaxPrice}
                                step={priceStep}
                                value={priceMin}
                                onChange={(e) => handlePriceMinChange(Number(e.target.value))}
                                className={styles['range-slider']}
                            />
                            <input
                                type="range"
                                min="0"
                                max={roundedMaxPrice}
                                step={priceStep}
                                value={priceMax}
                                onChange={(e) => handlePriceMaxChange(Number(e.target.value))}
                                className={styles['range-slider']}
                            />
                        </div>
                    </div>
                </div>
                {isPublicUser && user && (
                <div className="col-6 col-md-3">
                    <Button
                        classes={'w-100'}
                        icon="fa-solid fa-bell"
                        mode={ButtonMode.primary}
                        onClick={handleCreateAlert}
                        label={t('spaces-filter.create-alert')}
                        disabled={creatingAlert}
                    />
                </div>
                )}
                <div className="col-6 col-md-3">
                    <Button
                        classes={'w-100'}
                        mode={ButtonMode.primary}
                        outline
                        icon="fa-solid fa-trash"
                        onClick={handleClearFilters}
                        label={t('spaces-filter.clear')}
                    />
                </div>
            </div>
        </div>
    );

    return isMobile ? renderMobileFilters() : renderDesktopFilters();
};
